import { ComponentProps, useId, useMemo, useRef, useState } from "react";
import { ResultOf } from "gql.tada";
import { GetStartedSectionById } from "@queries/entries/GetStartedSectionById";
import { TextInput } from "@components/shared/core/TextInput";
import { Select } from "@components/shared/core/Select";
import { MultiSelect } from "@components/shared/core/MultiSelect";
import { Button } from "@components/shared/core/Button";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as y from "yup";
import { CheckboxInput } from "@components/shared/core/CheckboxInput";
import { FormLabelsContext } from "@components/shared/core/FormLabelsContext";
import { RichText } from "@components/shared/core/RichText";
import { useLenis } from "lenis/react";
import { useUpdateEffect } from "@react-hookz/web";
import { sendApiRequest } from "@services/sendApiRequest";
import cx from "classnames";
import { IconX } from "@hawkins/assets";
import { AnimatePresence, motion } from "motion/react";
import { Analytics } from "@utils/Analytics";
import { useGlobalContext } from "@utils/GlobalContext";

// if you change this, please change the corresponding validation in server/validation/NewLeadRequest.ts
const GetStartedFormValidator = y.object({
  firstName: y.string().required(),
  lastName: y.string().required(),
  email: y.string().email().required(),
  title: y.string().optional(),
  phoneNumber: y.string().nullable(),
  company: y.string().required(),
  inquiryType: y.string().required(),
  companyAdditional: y.string().nullable(),
  website: y.string().nullable(),
  agency: y.string().required(),
  industry: y.string().when("inquiryType", {
    is: "Support",
    then: (schema) => schema.nullable(),
    otherwise: (schema) => schema.required(),
  }),
  advertisingMarket: y
    .array()
    .of(y.string().required())
    .when("inquiryType", {
      is: "Support",
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.min(1).required(),
    }),
  usRegion: y.string().nullable(),
  campaignBudget: y.string().when("inquiryType", {
    is: "Support",
    then: (schema) => schema.nullable(),
    otherwise: (schema) => schema.required(),
  }),
  annualSpend: y.string().nullable(),
  programmaticDsp: y.string().when("inquiryType", {
    is: "Support",
    then: (schema) => schema.nullable(),
    otherwise: (schema) => schema.required(),
  }),
  additionalNotes: y.string().when(["inquiryType", "programmaticDsp"],
    ([inquiryType, programmaticDsp], schema) => {
    return inquiryType === "Support" || programmaticDsp === "Other (please detail in notes below)"
      ? schema.required()
      : schema.nullable();
  }),
  agreeToTerms: y.boolean().equals([true]).required(),
  agreeToNewsLetter: y.boolean(),
});

type GetStartedFormModel = y.InferType<typeof GetStartedFormValidator>;

export const GetStartedForm = ({
  entry,
  onSubmitSuccess,
  ...props
}: {
  entry: NonNullable<
    ResultOf<typeof GetStartedSectionById>["getStartedSection"]
  >;
  onSubmitSuccess?: () => void;
} & Omit<ComponentProps<"form">, "onSubmit" | "children">) => {
  const formRef = useRef<HTMLFormElement>(null);
  const termsLabelContentId = useId();
  const lenis = useLenis();
  const { locale } = useGlobalContext();
  const [showErrorToast, setShowErrorToast] = useState(false);
  const {
    register: hookFormRegister,
    handleSubmit,
    control,
    watch,
    formState: { isSubmitting, isSubmitted, isSubmitSuccessful, errors },
  } = useForm({
    resolver: yupResolver(GetStartedFormValidator),
  });

  const fieldDescriptions = GetStartedFormValidator.describe({
    value: watch(),
  }).fields;

  const isRequired = (name: keyof GetStartedFormModel): boolean =>
    // @ts-expect-error This property does exist
    fieldDescriptions[name]?.tests?.some(
      (test: { name: string }) =>
        test.name === "required" || test.name === "min"
    ) ?? false;

  const register = (name: keyof GetStartedFormModel) => ({
    ...hookFormRegister(name),
    required: isRequired(name),
  });

  const isInvalid = (name: keyof GetStartedFormModel) =>
    isSubmitted && !isSubmitSuccessful && !!errors[name];

  const inquiryTypeLabel = watch("inquiryType");
  const isSupportInquiry = inquiryTypeLabel !== "Support";

  const otherLabels = useMemo(
    () => ({
      required: entry.requiredFieldText!,
      selectPlaceholder: entry.selectOption!,
    }),
    [entry]
  );

  // On invalid submissions, scroll to invalid field
  useUpdateEffect(() => {
    if (!isSubmitting && !isSubmitSuccessful) {
      const form = formRef.current;
      if (!form) return;
      const errorKeys = Object.keys(errors);
      if (errorKeys.length === 0) return;
      const firstErrorFieldName = errorKeys[0];
      const element = form.querySelector(`[name="${firstErrorFieldName}"]`);
      if (!element || !(element instanceof HTMLElement)) return;
      element.focus();
      // This was having issues when done within the same event loop invocation
      setTimeout(
        () =>
          lenis?.scrollTo(
            element,
            // This gives enough room for the nav to not get in the way and properly display the label
            { offset: -150 }
          ),
        0
      );
    }
  }, [isSubmitting, isSubmitSuccessful]);

  async function handleNewsletterSignup(email: string) {
    const signUpData = { email };
    try {
      await sendApiRequest("/signup", signUpData);
    } catch (error) {
      console.error("Error signing up for newsletter:", error);
    }
  }

  async function handleLeadSubmission(formData: GetStartedFormModel) {
    const response = await sendApiRequest<{ success: boolean }>("/lead", formData);
    if (!response.success) {
      throw new Error("Lead submission failed");
    }
  }

  async function submitForm(data: GetStartedFormModel) {
    if (data.agreeToNewsLetter) {
      await handleNewsletterSignup(data.email);
    }

    try {
      await handleLeadSubmission(data);
    } catch (error) {
      console.error("Error submitting lead:", error);
      setShowErrorToast(true);
      throw error;
    }

    // Ensure viewport is at the top after a successful submission to avoid
    // landing near the footer when conditional fields collapse
    lenis?.scrollTo(0);

    onSubmitSuccess?.();
  }

  function handleSubmitClick() {
    Analytics.trackEvent(`get_connected_${locale}_form_submit_click`);
  }

  return (
    <FormLabelsContext.Provider value={otherLabels}>
      <AnimatePresence>
        {showErrorToast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            role="alert"
            className="fixed bottom-4 right-4 z-[200] flex items-center gap-3 rounded-lg bg-brand-red px-4 py-3 text-brand-cream shadow-lg"
          >
            <span className="text-body-regular">
              We ran into an issue submitting your request. Please try again.
            </span>
            <button
              onClick={() => setShowErrorToast(false)}
              className="shrink-0 rounded p-1 transition-opacity hover:opacity-70"
              aria-label="Dismiss"
            >
              <IconX size="small" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      <form
        ref={formRef}
        {...props}
        noValidate={props.noValidate == null ? true : props.noValidate}
        onSubmit={handleSubmit((data) => submitForm(data))}
      >
        <div className="flex flex-col gap-4">
          <TextInput
            label={entry.firstNameFieldLabel ?? ""}
            inputProps={register("firstName")}
            invalid={isInvalid("firstName")}
          />
          <TextInput
            label={entry.lastNameFieldLabel ?? ""}
            inputProps={register("lastName")}
            invalid={isInvalid("lastName")}
          />
          <TextInput
            label={entry.emailFieldLabel ?? ""}
            inputProps={{ ...register("email"), type: "email" }}
            invalid={isInvalid("email")}
          />
          <TextInput
            label={entry.titleFieldLabel ?? ""}
            inputProps={register("title")}
            invalid={isInvalid("title")}
          />
          <TextInput
            label={entry.businessPhoneNumberFieldLabel ?? ""}
            inputProps={{ ...register("phoneNumber"), type: "tel" }}
            invalid={isInvalid("phoneNumber")}
          />
          <TextInput
            label={entry.advertiserAgencyNameFieldLabel ?? ""}
            inputProps={register("company")}
            invalid={isInvalid("company")}
            subtext={entry.pleaseInputNa ?? ""}
          />

          <TextInput
            label={entry.agencyLabel ?? ""}
            inputProps={register("agency")}
            invalid={isInvalid("agency")}
            subtext={entry.pleaseInputNa ?? ""}
          />

          <Controller
            control={control}
            name="inquiryType"
            render={({ field }) => (
              <Select
                label={entry.inquiryTypeLabel ?? ""}
                options={
                  entry.inquiryTypeOptionsListCollection?.items?.filter(
                    Boolean
                  ) ?? []
                }
                required={isRequired(field.name)}
                value={field.value ?? ""}
                onChange={field.onChange}
                invalid={isInvalid(field.name)}
                name={field.name}
              />
            )}
          />

          {inquiryTypeLabel && (
            <>
              {isSupportInquiry && (
                <>
                  <TextInput
                    label={entry.additionalAdvertiserFieldLabel ?? ""}
                    inputProps={register("companyAdditional")}
                    invalid={isInvalid("companyAdditional")}
                  />
                  <TextInput
                    label={entry.websiteFieldLabel ?? ""}
                    inputProps={register("website")}
                    invalid={isInvalid("website")}
                  />
                  <Controller
                    control={control}
                    name="industry"
                    render={({ field }) => (
                      <Select
                        label={entry.industryVerticalFieldLabel ?? ""}
                        options={
                          entry.industryVerticalOptionsListCollection?.items?.filter(
                            Boolean
                          ) ?? []
                        }
                        required={isRequired(field.name)}
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        invalid={isInvalid(field.name)}
                        name={field.name}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="advertisingMarket"
                    render={({ field }) => (
                      <MultiSelect
                        label={entry.advertisingMarketCountryLabel ?? ""}
                        options={
                          entry.advertisingMarketCountryOptionsListCollection?.items?.filter(
                            Boolean
                          ) ?? []
                        }
                        required={isRequired(field.name)}
                        value={field.value ?? []}
                        onChange={field.onChange}
                        invalid={isInvalid(field.name)}
                        name={field.name}
                      />
                    )}
                  />

                  {/* TODO - We need to solve this for multiple languages */}
                  {watch("advertisingMarket")?.includes("United States") && (
                    <Controller
                      control={control}
                      name="usRegion"
                      render={({ field }) => (
                        <Select
                          label={entry.usRegionLabel ?? ""}
                          options={
                            entry.usRegionOptionsListCollection?.items?.filter(
                              Boolean
                            ) ?? []
                          }
                          required={isRequired(field.name)}
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          invalid={isInvalid(field.name)}
                          name={field.name}
                        />
                      )}
                    />
                  )}
                  <Controller
                    control={control}
                    name="campaignBudget"
                    render={({ field }) => (
                      <Select
                        label={entry.estimatedCampaignBudgetLabel ?? ""}
                        options={
                          entry.estimatedCampaignBudgetOptionsListCollection?.items?.filter(
                            Boolean
                          ) ?? []
                        }
                        required={isRequired(field.name)}
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        invalid={isInvalid(field.name)}
                        name={field.name}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="annualSpend"
                    render={({ field }) => (
                      <Select
                        label={entry.estimatedAnnualSpendOnNetflixLabel ?? ""}
                        options={
                          entry.estimatedAnnualSpendOnNetflixOptionsListCollection?.items?.filter(
                            Boolean
                          ) ?? []
                        }
                        required={isRequired(field.name)}
                        value={field.value || ""}
                        onChange={field.onChange}
                        invalid={isInvalid(field.name)}
                        name={field.name}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="programmaticDsp"
                    render={({ field }) => (
                      <Select
                        label={entry.preferredProgrammaticDspUsedLabel ?? ""}
                        options={
                          entry.preferredProgrammaticDspUsedOptionsListCollection?.items?.filter(
                            Boolean
                          ) ?? []
                        }
                        required={isRequired(field.name)}
                        value={field.value || ""}
                        onChange={field.onChange}
                        invalid={isInvalid(field.name)}
                        name={field.name}
                      />
                    )}
                  />
                </>
              )}

              <TextInput
                label={entry.additionalNotesLabel ?? ""}
                inputProps={register("additionalNotes")}
                invalid={isInvalid("additionalNotes")}
              />

              <Controller
                control={control}
                name="agreeToNewsLetter"
                render={({ field }) => (
                  <CheckboxInput
                    label={entry.newsletterSignUpLabel ?? ""}
                    inputProps={{ ...field }}
                    invalid={isInvalid(field.name)}
                  />
                )}
              />

              <Controller
                control={control}
                name="agreeToTerms"
                render={({ field }) => (
                  <div className="flex">
                    <CheckboxInput
                      label={<span aria-describedby={termsLabelContentId} />}
                      inputProps={{ ...field }}
                      invalid={isInvalid(field.name)}
                    />
                    <div
                      className={cx(
                        "transition-colors",
                        isInvalid(field.name) && "text-brand-red"
                      )}
                      id={termsLabelContentId}
                    >
                      <RichText content={entry.termsConditionsCheckboxLabel} />
                    </div>
                  </div>
                )}
              />

              <Button
                type="submit"
                variant="light-on-true"
                className="dsk:w-[247px] mt-5 w-full"
                loading={isSubmitting}
                disabled={isSubmitSuccessful}
                onClick={handleSubmitClick}
              >
                {isSubmitSuccessful
                  ? entry.submittedLabel
                  : entry.submitButtonLabel}
              </Button>
            </>
          )}
        </div>
      </form>
    </FormLabelsContext.Provider>
  );
};
